<script lang="ts">
	//Design system components
	import {
		Alert,
		CardGrid,
		Collapse,
		Container,
		Input,
		SelectCard,
		Stack,
		Text,
	} from '@Shelly/shelly-design-system';
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	//Constants
	import { TEMPERATURE_SENSORS } from 'src/constants';
	//Helpers
	import { hasExternalSensors } from 'src/helpers/ext_sensor_helpers';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { generateURI, parseID } from '../helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		filterDevicesByDeviceName,
		sortRoomsWithMissingDevices,
	} from 'src/components/Lists/list-helpers';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	//Stores
	import { createMemberSettingsSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import type { Device } from 'src/types/Device';
	//Svelte hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import NoSearchResults from 'src/components/Lists/NoSearchResults.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let editableObject;
	let sensorID = parseID(editableObject.thermostat.sensor);
	let deviceFilter = '';
	let editGroup = { devices: [sensorID] };

	let listDevByGroupType = [];

	let allItemsRefs: { [key: string | number]: HTMLElement } = {};

	const cachedCurrentGroupDevices = editGroup.devices;

	let mappedListDevicesByRoomID: {
		roomID?: string | number;
		roomName?: String;
		devices?: {
			name: string;
			id: string | number;
			image?: string;
			backgroundColor?: AvatarColorVariantTypes;
			icon?: string;
			selected: boolean;
			roomName: string;
			roomID: string | number;
		}[];
	}[] = [];

	$: memberSettingsStore = createMemberSettingsSnapshot(
		Object.values($devices).map((d: Device) => d.id),
	);
	$: memberSettings = $memberSettingsStore;

	$: listDevByGroupType = [
		...Object.values($devices).filter((d: Device) => {
			return (
				!d.shared &&
				typeof memberSettings[real_id(d.id)] !== 'undefined' &&
				((TEMPERATURE_SENSORS.includes(d.type) &&
					d.type !==
						'SHMOS-02') /**explicitly removing SHMOS because wall display doesn't support it!!*/ ||
					hasExternalSensors(d))
			);
		}),
	];

	let roomsExpanded = {};

	$: mappedListDevicesByRoomID = filterDevicesByRoomID(
		filterDevicesByDeviceName(listDevByGroupType, deviceFilter),
	);

	onMount(() => {
		roomsExpanded = mappedListDevicesByRoomID.reduce(
			(o, room) => ({
				...o,
				[room.roomID]: room.devices.some((device) => device.selected),
			}),
			{},
		);
	});

	const filterDevicesByRoomID = (array: Device[]) => {
		let filteredDevicesByRoom = {};

		const deletedDevices = cachedCurrentGroupDevices.filter(
			(dId) => 'undefined' === typeof $devices[dId],
		);

		array.forEach((device: Device, i) => {
			const roomID = device.room_id;

			const deviceData = {
				name: device.name,
				id: device.id,
				selected: editGroup.devices.findIndex((el) => el === device.id) !== -1,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image,
				backgroundColor: device.backgroundColor,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID].devices = [
					...filteredDevicesByRoom[roomID].devices,
					deviceData,
				];
			} else {
				filteredDevicesByRoom[roomID] = {
					devices: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
				};
			}
		});

		if (deletedDevices.length) {
			deletedDevices.forEach((id) => {
				const deviceData = {
					name: id,
					id,
					selected: editGroup.devices.findIndex((el) => el === id) !== -1,
					roomName: $format('missing_devices_label', {
						default: 'Missing Devices',
					}),
					roomID: 'missing_devices',
					icon: 'text-warning fas fa-warning',
				};

				if (filteredDevicesByRoom['missing_devices']) {
					filteredDevicesByRoom['missing_devices'].devices = [
						...filteredDevicesByRoom['missing_devices'].devices,
						deviceData,
					];
				} else {
					filteredDevicesByRoom['missing_devices'] = {
						devices: [deviceData],
						roomName: $format('missing_devices_label', {
							default: 'Missing Devices',
						}),
						roomID: 'missing_devices',
					};
				}
			});
		}
		return sortRoomsWithMissingDevices(filteredDevicesByRoom);
	};

	const onSelectDevice = (device) => {
		const selectedDeviceId = device.id;
		const currenDeviceRoomID = device.roomID;
		mappedListDevicesByRoomID = mappedListDevicesByRoomID.map((room: any) => {
			if (room.roomID === currenDeviceRoomID) {
				return {
					...room,
					devices: room.devices.map((d) => {
						if (d.id === selectedDeviceId) {
							return {
								...d,
								selected: !d.selected,
							};
						} else {
							return { ...d, selected: false };
						}
					}),
				};
			} else {
				room.devices.map((d) => (d.selected = false));
			}

			return room;
		});
		editableObject.thermostat.sensor = generateURI(
			editableObject.thermostat.sensor,
			selectedDeviceId,
		);
	};
</script>

<Container padding="scrollPaddings">
	<Stack
		gap="s"
		class="mb-l"
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
	>
		<Text element="h2" noWrap class="align-self-center"
			>{$format('select_thermostat_sensor', { default: 'Select sensor' })}</Text
		>
		<Input
			type="text"
			label={capFirstChar($format('search'))}
			icon="fas fa-search"
			class="width-lg-l width-sm-full"
			bind:value={deviceFilter}
			id="input-wj1mfgr"
			aria-labelledby="input-wj1mfgr"
		/>
	</Stack>

	{#if mappedListDevicesByRoomID.length > 0}
		{#each mappedListDevicesByRoomID as room, ind}
			<Collapse
				id="collapse-xh3j102"
				{...ind !== mappedListDevicesByRoomID.length - 1 ? { class: 'mb-s' } : {}}
				title={'' + room.roomName}
				expanded={roomsExpanded[room.roomID]}
				on:click={() => {
					roomsExpanded[room.roomID] = !roomsExpanded[room.roomID];
				}}
			>
				{#if room.roomID === 'missing_devices'}
					<Alert
						type="warning"
						class="mb-s"
						heading={$format('missing_devices_alert_label ', {
							values: {
								type: $format('device_type_thermostat', {
									default: 'thermostat',
								}),
							},
							default: 'You have missing device(s) in this {type}.',
						})}
					/>
				{/if}
				<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
					{#each room.devices as device (device.id)}
						<div class="d-flex" bind:this={allItemsRefs[device.id]}>
							<SelectCard
								cardBgColor={$showNewLayout ? 'color-2' : undefined}
								type="horizontal"
								class="width-full"
								title={device.name}
								variant={'checkbox'}
								image={device.image
									? load_image_from(device.image, device?.server)
									: undefined}
								backgroundColor={device.backgroundColor}
								selected={device.selected}
								on:click={() => onSelectDevice(device)}
								icon={device.icon ? device.icon : undefined}
							/>
						</div>
					{/each}
				</CardGrid>
			</Collapse>
		{/each}
	{:else}
		<NoSearchResults search={deviceFilter} />
	{/if}
</Container>
